/*
 * RRE_FRONTEND_V1_0_20260317.jsx
 * Project: Resh Resume Engine (RRE)
 * Component: React Frontend — 5-Step Verified Resume Builder
 * Version: 1.0
 * Date: 2026-03-17
 * Inventor: Vijay Anand Raj Kanaparthy
 * Governing Scripture: John 14:26
 * PVD Phase: 3 — Build
 *
 * Design: Biblical color scheme — deep navy, gold, white, green
 * Design Law #1: Common person uses it on day one without training
 * Design Law #3: Human sees, traces, verifies, and stops it
 *
 * Steps:
 *   1 — Who Are You        (name + target role)
 *   2 — Verify Identity    (zero-retention phone + ID type)
 *   3 — Work Experience    (claims → 4-signal verification)
 *   4 — Skills             (free-form skill entry)
 *   5 — Download           (verified resume PDF + RRE ID)
 */

import { useState, useEffect, useRef } from "react";

const API = "https://rre-backend.onrender.com";

// ── Color tokens ──────────────────────────────────────────
const DARK = {
  navy:      "#0D1B2A",
  navyMid:   "#1B3A6B",
  gold:      "#D4AF37",
  goldLight: "#F0D060",
  white:     "#F5F5F0",
  green:     "#2E7D52",
  greenLight:"#4CAF80",
  gray:      "#8899AA",
  grayLight: "#C8D4E0",
  error:     "#C0392B",
  card:      "#122035",
  border:    "#1E3A5F",
};
const LIGHT = {
  navy:      "#F5F0E8",
  navyMid:   "#E8E0D0",
  gold:      "#7A5C0A",
  goldLight: "#8B6914",
  white:     "#0A0A1A",
  green:     "#1A5C35",
  greenLight:"#2E7D4F",
  gray:      "#2D3748",
  grayLight: "#1A202C",
  error:     "#C0392B",
  card:      "#FFFFFF",
  border:    "#C9A84C",
};
// C is set dynamically inside the component based on darkMode state

// ── Styles ────────────────────────────────────────────────
const S = {
  app: {
    minHeight: "100vh",
    background: DARK.navy,
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: DARK.white,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px 60px",
  },
  header: {
    width: "100%",
    maxWidth: 720,
    textAlign: "center",
    padding: "48px 0 32px",
    borderBottom: `1px solid ${DARK.border}`,
    marginBottom: 40,
  },
  logo: {
    fontSize: 11,
    letterSpacing: 4,
    color: DARK.gold,
    textTransform: "uppercase",
    marginBottom: 10,
    fontFamily: "'Georgia', serif",
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: DARK.white,
    margin: "0 0 8px",
    letterSpacing: 1,
  },
  subtitle: {
    fontSize: 14,
    color: DARK.gray,
    margin: 0,
    lineHeight: 1.6,
  },
  progressBar: {
    width: "100%",
    maxWidth: 720,
    marginBottom: 36,
  },
  progressTrack: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
  },
  progressLine: {
    position: "absolute",
    top: 18,
    left: "10%",
    right: "10%",
    height: 2,
    background: DARK.border,
    zIndex: 0,
  },
  progressFill: (pct) => ({
    position: "absolute",
    top: 18,
    left: "10%",
    width: `${pct * 80}%`,
    height: 2,
    background: DARK.gold,
    zIndex: 1,
    transition: "width 0.5s ease",
  }),
  stepDot: (active, done) => ({
    width: 36,
    height: 36,
    borderRadius: "50%",
    background: done ? DARK.green : active ? DARK.gold : DARK.card,
    border: `2px solid ${done ? DARK.green : active ? DARK.gold : DARK.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: "bold",
    color: done || active ? DARK.navy : DARK.gray,
    zIndex: 2,
    position: "relative",
    transition: "all 0.3s ease",
    flexShrink: 0,
  }),
  stepLabel: (active, done) => ({
    fontSize: 10,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: done ? DARK.greenLight : active ? DARK.gold : DARK.gray,
    marginTop: 6,
    textAlign: "center",
    maxWidth: 72,
  }),
  stepCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flex: 1,
  },
  card: {
    width: "100%",
    maxWidth: 720,
    background: DARK.card,
    border: `1px solid ${DARK.border}`,
    borderRadius: 12,
    padding: "36px 40px",
    marginBottom: 24,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: DARK.gold,
    marginBottom: 6,
    letterSpacing: 0.5,
  },
  cardSubtitle: {
    fontSize: 13,
    color: DARK.gray,
    marginBottom: 28,
    lineHeight: 1.6,
  },
  label: {
    display: "block",
    fontSize: 11,
    letterSpacing: 2,
    textTransform: "uppercase",
    color: DARK.grayLight,
    marginBottom: 7,
    marginTop: 20,
  },
  input: {
    width: "100%",
    background: "#0A1520",
    border: `1px solid ${DARK.border}`,
    borderRadius: 6,
    padding: "12px 14px",
    color: DARK.white,
    fontSize: 15,
    fontFamily: "'Georgia', serif",
    outline: "none",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  select: {
    width: "100%",
    background: "#0A1520",
    border: `1px solid ${DARK.border}`,
    borderRadius: 6,
    padding: "12px 14px",
    color: DARK.white,
    fontSize: 15,
    fontFamily: "'Georgia', serif",
    outline: "none",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  textarea: {
    width: "100%",
    background: "#0A1520",
    border: `1px solid ${DARK.border}`,
    borderRadius: 6,
    padding: "12px 14px",
    color: DARK.white,
    fontSize: 14,
    fontFamily: "'Georgia', serif",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    minHeight: 80,
    lineHeight: 1.6,
  },
  btnPrimary: {
    background: DARK.gold,
    color: DARK.navy,
    border: "none",
    borderRadius: 6,
    padding: "14px 32px",
    fontSize: 14,
    fontWeight: "bold",
    fontFamily: "'Georgia', serif",
    letterSpacing: 1,
    cursor: "pointer",
    transition: "all 0.2s",
    textTransform: "uppercase",
  },
  btnSecondary: {
    background: "transparent",
    color: DARK.grayLight,
    border: `1px solid ${DARK.border}`,
    borderRadius: 6,
    padding: "12px 24px",
    fontSize: 13,
    fontFamily: "'Georgia', serif",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  btnGreen: {
    background: DARK.green,
    color: DARK.white,
    border: "none",
    borderRadius: 6,
    padding: "14px 32px",
    fontSize: 14,
    fontWeight: "bold",
    fontFamily: "'Georgia', serif",
    letterSpacing: 1,
    cursor: "pointer",
    transition: "all 0.2s",
    textTransform: "uppercase",
  },
  btnRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 32,
    gap: 12,
  },
  errorBox: {
    background: "rgba(192,57,43,0.12)",
    border: `1px solid ${DARK.error}`,
    borderRadius: 6,
    padding: "12px 16px",
    color: "#E57373",
    fontSize: 13,
    marginTop: 16,
    lineHeight: 1.5,
  },
  infoBox: {
    background: "rgba(212,175,55,0.08)",
    border: `1px solid rgba(212,175,55,0.3)`,
    borderRadius: 6,
    padding: "12px 16px",
    color: DARK.gold,
    fontSize: 12,
    marginTop: 16,
    lineHeight: 1.6,
  },
  successBox: {
    background: "rgba(46,125,82,0.12)",
    border: `1px solid ${DARK.green}`,
    borderRadius: 6,
    padding: "12px 16px",
    color: DARK.greenLight,
    fontSize: 13,
    marginTop: 16,
    lineHeight: 1.5,
  },
  experienceCard: {
    background: "#0A1520",
    border: `1px solid ${DARK.border}`,
    borderRadius: 8,
    padding: "20px 20px 16px",
    marginBottom: 16,
  },
  verdictBadge: (verdict) => ({
    display: "inline-block",
    fontSize: 10,
    fontWeight: "bold",
    letterSpacing: 2,
    padding: "3px 10px",
    borderRadius: 20,
    textTransform: "uppercase",
    background: verdict === "VERIFIED" ? "rgba(46,125,82,0.2)"
              : verdict === "FLAGGED"  ? "rgba(212,175,55,0.2)"
              : "rgba(192,57,43,0.2)",
    color: verdict === "VERIFIED" ? DARK.greenLight
         : verdict === "FLAGGED"  ? DARK.gold
         : "#E57373",
    border: `1px solid ${
      verdict === "VERIFIED" ? DARK.green
      : verdict === "FLAGGED" ? DARK.gold
      : DARK.error}`,
    marginLeft: 10,
  }),
  skillTag: {
    display: "inline-flex",
    alignItems: "center",
    background: "rgba(212,175,55,0.1)",
    border: `1px solid rgba(212,175,55,0.3)`,
    borderRadius: 20,
    padding: "5px 14px 5px 14px",
    fontSize: 13,
    color: DARK.gold,
    margin: "4px 6px 4px 0",
    gap: 8,
  },
  skillX: {
    cursor: "pointer",
    color: DARK.gray,
    fontSize: 15,
    lineHeight: 1,
    fontWeight: "bold",
  },
  divider: {
    height: 1,
    background: DARK.border,
    margin: "24px 0",
  },
  scripture: {
    fontSize: 11,
    color: DARK.gray,
    textAlign: "center",
    letterSpacing: 1,
    marginTop: 48,
    paddingTop: 24,
    borderTop: `1px solid ${DARK.border}`,
    width: "100%",
    maxWidth: 720,
  },
};

// ── Step labels ────────────────────────────────────────────
const STEPS = ["Who Are You", "Verify", "Experience", "Skills", "Download"];

// ── Main App ──────────────────────────────────────────────
export default function RREApp() {
  const [darkMode, setDarkMode]   = useState(true);
  const C = darkMode ? DARK : LIGHT;
  const cardStyle = {...S.card, background: C.card, border: `1px solid ${C.border}`};
  const titleStyle = {...S.title, color: C.white};
  const subtitleStyle = {...S.subtitle, color: C.gray};
  const labelStyle = {...S.label, color: C.gray};
  const inputStyle = {...S.input, background: darkMode ? "#0A1520" : "#FFFFFF", color: C.white, border: `1px solid ${C.border}`};
  const selectStyle = {...S.select, background: darkMode ? "#0A1520" : "#FFFFFF", color: C.white, border: `1px solid ${C.border}`};
  const [step, setStep]           = useState(1);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState("");

  // Step 1
  const [fullName, setFullName]     = useState("");
  const [targetRole, setTargetRole] = useState("");

  // Step 2
  const [phone, setPhone]         = useState("");
  const [govIdType, setGovIdType] = useState("DL");
  const [identityHash, setIdentityHash] = useState("");
  const [identityAnchored, setIdentityAnchored] = useState(false);

  // Step 3
  const [experiences, setExperiences] = useState([
    { title: "", company: "", start_year: "", end_year: "", description: "" }
  ]);
  const [canonResult, setCanonResult] = useState(null);

  // Step 4
  const [skillInput, setSkillInput] = useState("");
  const [skills, setSkills]         = useState([]);
  const [uploadStatus, setUploadStatus] = useState("");
  const fileInputRef = useRef(null);

  // Step 5
  const [rreId, setRreId]         = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");
  const [verifyUrl, setVerifyUrl] = useState("");

  // Start session on mount
  useEffect(() => {
    fetch(`${API}/api/rre/session/start`, { method: "POST",
      headers: { "Content-Type": "application/json" }, body: "{}" })
      .then(r => r.json())
      .then(d => setSessionId(d.session_id))
      .catch(() => setError("Cannot reach RRE backend. Is rre_app.py running on port 5001?"));
  }, []);

  const clearError = () => setError("");

  // ── Upload resume for pre-fill (Layer 1) ───────────────────
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadStatus("Parsing your resume...");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await fetch(`${API}/api/rre/resume/upload`, { method: "POST", body: formData });
      const data = await res.json();
      if (data.status === "PARSED") {
        if (data.detected_name && !fullName) setFullName(data.detected_name);
        if (data.detected_skills && data.detected_skills.length > 0) setSkills(data.detected_skills);
        setUploadStatus(`✦ Resume parsed — ${data.raw_text_length} characters read. Fields pre-filled where detected.`);
      } else {
        setUploadStatus("Could not parse resume. Fill in manually.");
      }
    } catch {
      setUploadStatus("Upload failed. Fill in manually.");
    }
  };

  // ── Step 1 → 2: Basic info ───────────────────────────────
  const handleStep1 = () => {
    if (!fullName.trim()) { setError("Please enter your full name."); return; }
    if (!targetRole.trim()) { setError("Please enter the role you are targeting."); return; }
    clearError();
    setStep(2);
  };

  // ── Step 2: Verify identity ──────────────────────────────
  const handleStep2 = async () => {
    if (!phone.trim() || phone.replace(/\D/g, "").length < 10) {
      setError("Please enter a valid 10-digit phone number."); return;
    }
    clearError();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/rre/identity/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          phone: phone.replace(/\D/g, ""),
          gov_id_type: govIdType,
          full_name: fullName,
          target_role: targetRole,
        }),
      });
      const data = await res.json();
      if (data.status === "DUPLICATE") {
        setError("A verified identity record already exists for this person. One real human = one RRE record.");
      } else if (data.status === "ANCHORED") {
        setIdentityHash(data.identity_hash);
        setIdentityAnchored(true);
        setStep(3);
      } else {
        setError(data.error || "Identity verification failed.");
      }
    } catch {
      setError("Could not reach RRE backend.");
    }
    setLoading(false);
  };

  // ── Step 3: Experience claims ────────────────────────────
  const addExperience = () => {
    setExperiences([...experiences,
      { title: "", company: "", start_year: "", end_year: "", description: "" }]);
  };
  const removeExperience = (i) => {
    if (experiences.length === 1) return;
    setExperiences(experiences.filter((_, idx) => idx !== i));
  };
  const updateExp = (i, field, val) => {
    const updated = [...experiences];
    updated[i][field] = val;
    setExperiences(updated);
  };

  const handleStep3 = async () => {
    const valid = experiences.filter(e => e.title.trim() && e.company.trim());
    if (valid.length === 0) {
      setError("Please fill in at least one position with a title and company."); return;
    }
    clearError();
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/rre/canon/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          experience_claims: experiences,
          skills: skills,
        }),
      });
      const data = await res.json();
      if (data.status === "PROCESSED") {
        setCanonResult(data);
        setStep(4);
      } else {
        setError(data.error || "Experience verification failed.");
      }
    } catch {
      setError("Could not reach RRE backend.");
    }
    setLoading(false);
  };

  // ── Step 4: Skills ───────────────────────────────────────
  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !skills.includes(s)) setSkills([...skills, s]);
    setSkillInput("");
  };
  const removeSkill = (s) => setSkills(skills.filter(x => x !== s));
  const handleSkillKey = (e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } };

  const handleStep4 = async () => {
    clearError();
    setLoading(true);
    try {
      // Re-submit canon with final skills list
      const res = await fetch(`${API}/api/rre/canon/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionId,
          experience_claims: experiences,
          skills: skills,
        }),
      });
      await res.json();

      // Generate resume
      const genRes = await fetch(`${API}/api/rre/resume/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const genData = await genRes.json();
      if (genData.status === "READY") {
        setRreId(genData.rre_id);
        setDownloadUrl(`${API}${genData.download_url}`);
        setVerifyUrl(`${API}${genData.verify_url}`);
        setStep(5);
      } else {
        setError(genData.error || "Resume generation failed.");
      }
    } catch {
      setError("Could not reach RRE backend.");
    }
    setLoading(false);
  };

  // ── Progress pct ─────────────────────────────────────────
  const progressPct = (step - 1) / (STEPS.length - 1);

  return (
    <div style={{...S.app, background: C.navy, color: C.white}}>

      {/* Header */}
      <div style={S.header}>
        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", width:"100%", marginBottom:8}}>
          <div style={S.logo}>Resh Consultancy LLC</div>
          <button
            onClick={() => setDarkMode(d => !d)}
            style={{
              background: "none",
              border: `1px solid ${C.border}`,
              borderRadius: 20,
              padding: "4px 14px",
              cursor: "pointer",
              color: C.gold,
              fontSize: 13,
              fontFamily: "Georgia, serif",
              letterSpacing: 1,
            }}
          >
            {darkMode ? "☀ Light" : "☾ Dark"}
          </button>
        </div>
        <h1 style={titleStyle}>Resh Resume Engine™</h1>
        <p style={subtitleStyle}>
          Your resume, verified. Every claim checked. Every page protected.<br/>
          Built on truth. Delivered with proof.
        </p>
      </div>

      {/* Progress */}
      <div style={S.progressBar}>
        <div style={S.progressTrack}>
          <div style={S.progressLine} />
          <div style={S.progressFill(progressPct)} />
          {STEPS.map((label, i) => (
            <div key={i} style={S.stepCol}>
              <div style={S.stepDot(step === i + 1, step > i + 1)}>
                {step > i + 1 ? "✓" : i + 1}
              </div>
              <div style={S.stepLabel(step === i + 1, step > i + 1)}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── STEP 1: Who Are You ── */}
      {step === 1 && (
        <div style={cardStyle}>
          <div style={S.cardTitle}>Who Are You?</div>
          <div style={S.cardSubtitle}>
            Tell us your name and the role you are going after.
            No account. No password. No personal data asked yet.
          </div>

          {/* Upload existing resume for pre-fill */}
          <div style={{marginBottom: 20, padding: "16px", background: darkMode ? "#0A1520" : "#F0EDE4", borderRadius: 8, border: `1px dashed ${C.border}`}}>
            <div style={{fontSize: 13, color: C.gray, marginBottom: 8}}>
              ✦ Optional — Upload your existing resume to pre-fill fields
            </div>
            <input
              type="file"
              accept=".pdf,.docx,.doc,.txt"
              ref={fileInputRef}
              style={{display:"none"}}
              onChange={handleUpload}
            />
            <button
              style={{...S.btnSecondary, fontSize: 13, padding: "8px 20px"}}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
            >
              📄 Upload Resume (PDF, DOCX, TXT)
            </button>
            {uploadStatus && <div style={{fontSize: 12, color: C.gold, marginTop: 8}}>{uploadStatus}</div>}
          </div>
          <label style={labelStyle}>Your Full Name</label>
          <input style={inputStyle} placeholder="e.g. Jane Smith"
            value={fullName} onChange={e => setFullName(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleStep1()} />

          <label style={labelStyle}>Role You Are Targeting</label>
          <input style={inputStyle} placeholder="e.g. Senior Software Engineer, Director of Finance"
            value={targetRole} onChange={e => setTargetRole(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleStep1()} />

          {error && <div style={S.errorBox}>{error}</div>}

          <div style={S.infoBox}>
            ✦ RRE never stores your SSN, government ID, or biometric data.
            Your identity is anchored via a one-way cryptographic hash — the
            original data is discarded immediately after hashing.
          </div>

          <div style={{...S.btnRow, justifyContent: "flex-end"}}>
            <button style={S.btnPrimary} onClick={handleStep1}>
              Continue →
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 2: Verify Identity ── */}
      {step === 2 && (
        <div style={cardStyle}>
          <div style={S.cardTitle}>Verify Your Identity</div>
          <div style={S.cardSubtitle}>
            One real person. One verified resume. Your phone number and
            government ID type are hashed immediately and never stored.
          </div>

          <label style={labelStyle}>Mobile Phone Number</label>
          <input style={inputStyle} placeholder="e.g. (214) 555-0100"
            value={phone} onChange={e => setPhone(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleStep2()} />

          <label style={labelStyle}>Government ID Type</label>
          <select style={selectStyle} value={govIdType}
            onChange={e => setGovIdType(e.target.value)}>
            <option value="DL">Driver's License</option>
            <option value="PASSPORT">Passport</option>
            <option value="STATE_ID">State ID</option>
            <option value="MILITARY_ID">Military ID</option>
          </select>

          <div style={S.infoBox}>
            ✦ We do not ask for your ID number. Only the ID type is used
            in the hash alongside your phone number. Nothing is transmitted
            to any third party. Nothing is stored in plain text — ever.
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <div style={S.btnRow}>
            <button style={S.btnSecondary} onClick={() => { clearError(); setStep(1); }}>
              ← Back
            </button>
            <button style={loading ? {...S.btnPrimary, opacity: 0.6} : S.btnPrimary}
              onClick={handleStep2} disabled={loading}>
              {loading ? "Verifying…" : "Verify & Continue →"}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 3: Work Experience ── */}
      {step === 3 && (
        <div style={cardStyle}>
          <div style={S.cardTitle}>Your Work Experience</div>
          <div style={S.cardSubtitle}>
            Tell us where you have worked and what you did.
            Be specific — describe your real work and outcomes.
            Every claim is verified before it appears on your resume.
          </div>

          {experiences.map((exp, i) => (
            <div key={i} style={S.experienceCard}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
                <span style={{color: C.gold, fontSize:12, letterSpacing:2, textTransform:"uppercase"}}>
                  Position {i + 1}
                </span>
                {experiences.length > 1 && (
                  <button onClick={() => removeExperience(i)}
                    style={{background:"none",border:"none",color:C.gray,cursor:"pointer",fontSize:18}}>
                    ×
                  </button>
                )}
              </div>

              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                <div>
                  <label style={{...labelStyle, marginTop:0}}>Job Title</label>
                  <input style={inputStyle} placeholder="e.g. Senior Engineer"
                    value={exp.title} onChange={e => updateExp(i, "title", e.target.value)} />
                </div>
                <div>
                  <label style={{...labelStyle, marginTop:0}}>Company</label>
                  <input style={inputStyle} placeholder="e.g. PNC Bank"
                    value={exp.company} onChange={e => updateExp(i, "company", e.target.value)} />
                </div>
                <div>
                  <label style={{...labelStyle, marginTop:0}}>Start Year</label>
                  <input style={inputStyle} placeholder="e.g. 2019"
                    value={exp.start_year} onChange={e => updateExp(i, "start_year", e.target.value)} />
                </div>
                <div>
                  <label style={{...labelStyle, marginTop:0}}>End Year</label>
                  <input style={inputStyle} placeholder="Leave blank if current"
                    value={exp.end_year} onChange={e => updateExp(i, "end_year", e.target.value)} />
                </div>
              </div>

              <label style={{...labelStyle}}>What did you do? What was the outcome?</label>
              <textarea style={S.textarea}
                placeholder="Describe your real work. Include what you built, led, improved, or delivered. The more specific you are, the stronger your verified resume."
                value={exp.description} onChange={e => updateExp(i, "description", e.target.value)} />
            </div>
          ))}

          <button onClick={addExperience}
            style={{...S.btnSecondary, width:"100%", textAlign:"center", padding:"10px"}}>
            + Add Another Position
          </button>

          {error && <div style={S.errorBox}>{error}</div>}

          <div style={S.btnRow}>
            <button style={S.btnSecondary} onClick={() => { clearError(); setStep(2); }}>
              ← Back
            </button>
            <button style={loading ? {...S.btnPrimary, opacity:0.6} : S.btnPrimary}
              onClick={handleStep3} disabled={loading}>
              {loading ? "Verifying Claims…" : "Verify & Continue →"}
            </button>
          </div>
        </div>
      )}

      {/* Verification results (shown before step 4) */}
      {step === 4 && canonResult && (
        <div style={{...cardStyle, marginBottom:0, borderBottom:"none",
          borderBottomLeftRadius:0, borderBottomRightRadius:0}}>
          <div style={S.cardTitle}>Verification Results</div>
          <div style={S.cardSubtitle}>
            {canonResult.verified_count} verified · {canonResult.flagged_count} flagged · {canonResult.rejected_count} rejected
          </div>
          {canonResult.claim_results.map((r, i) => (
            <div key={i} style={{marginBottom:10}}>
              <span style={{fontSize:13, color: C.white}}>{r.title} at {r.company}</span>
              <span style={S.verdictBadge(r.verdict)}>{r.verdict}</span>
              {r.issues.length > 0 && (
                <div style={{fontSize:12, color: C.gray, marginTop:4, lineHeight:1.5}}>
                  {r.issues.map((issue, j) => <div key={j}>• {issue}</div>)}
                </div>
              )}
            </div>
          ))}
          {canonResult.market_fit && (
            <div style={{...S.successBox, marginTop:16}}>
              ✦ {canonResult.market_fit.positioning}
            </div>
          )}
        </div>
      )}

      {/* ── STEP 4: Skills ── */}
      {step === 4 && (
        <div style={{...cardStyle, borderTopLeftRadius:0, borderTopRightRadius:0,
          borderTop:"none", paddingTop:24}}>
          <div style={{...S.divider, marginTop:0}} />
          <div style={S.cardTitle}>Your Skills</div>
          <div style={S.cardSubtitle}>
            Add your technical and professional skills. Press Enter or click Add after each one.
          </div>

          <div style={{display:"flex", gap:10}}>
            <input style={{...inputStyle, flex:1}} placeholder="e.g. Python, Project Management, COBOL"
              value={skillInput} onChange={e => setSkillInput(e.target.value)}
              onKeyDown={handleSkillKey} />
            <button style={{...S.btnSecondary, whiteSpace:"nowrap"}} onClick={addSkill}>
              Add
            </button>
          </div>

          <div style={{marginTop:16, minHeight:40}}>
            {skills.map((s, i) => (
              <span key={i} style={S.skillTag}>
                {s}
                <span style={S.skillX} onClick={() => removeSkill(s)}>×</span>
              </span>
            ))}
            {skills.length === 0 && (
              <span style={{color: C.gray, fontSize:13}}>
                No skills added yet. Skills are optional but strengthen your resume.
              </span>
            )}
          </div>

          {error && <div style={S.errorBox}>{error}</div>}

          <div style={S.btnRow}>
            <button style={S.btnSecondary} onClick={() => { clearError(); setStep(3); }}>
              ← Back
            </button>
            <button style={loading ? {...S.btnGreen, opacity:0.6} : S.btnGreen}
              onClick={handleStep4} disabled={loading}>
              {loading ? "Building Your Resume…" : "Generate Verified Resume →"}
            </button>
          </div>
        </div>
      )}

      {/* ── STEP 5: Download ── */}
      {step === 5 && (
        <div style={cardStyle}>
          <div style={{textAlign:"center", padding:"8px 0 24px"}}>
            <div style={{fontSize:48, marginBottom:16}}>✦</div>
            <div style={{...S.cardTitle, textAlign:"center", fontSize:24}}>
              Your Resume Is Ready
            </div>
            <div style={{...S.cardSubtitle, textAlign:"center"}}>
              Verified by Resh Resume Engine™. Every claim checked.
              Every page watermarked. Every resume traceable.
            </div>
          </div>

          <div style={S.divider} />

          <div style={{marginBottom:20}}>
            <div style={{fontSize:11, letterSpacing:2, textTransform:"uppercase",
              color: C.gray, marginBottom:6}}>Your RRE ID</div>
            <div style={{fontSize:18, fontWeight:"bold", color: C.gold,
              letterSpacing:2, fontFamily:"monospace"}}>
              {rreId}
            </div>
            <div style={{fontSize:12, color: C.gray, marginTop:6}}>
              This ID is embedded in your resume and in the verification registry.
              Any recruiter can verify your resume by scanning the QR code on the last page.
            </div>
          </div>

          <div style={S.divider} />

          <div style={{display:"flex", gap:12, flexWrap:"wrap"}}>
            <a href={downloadUrl} target="_blank" rel="noopener noreferrer"
              style={{...S.btnGreen, textDecoration:"none", display:"inline-block"}}>
              ↓ Download Verified Resume
            </a>
            <a href={verifyUrl} target="_blank" rel="noopener noreferrer"
              style={{...S.btnSecondary, textDecoration:"none", display:"inline-block"}}>
              ↗ Open Verification Portal
            </a>
          </div>

          <div style={S.infoBox}>
            ✦ Your resume carries a D.A.V.I.D. cryptographic watermark on every page.
            The QR code on the last page links directly to your verification record.
            AUTHENTIC · TAMPERED · NOT FOUND — any recruiter can check in seconds.
          </div>

          <div style={{...S.btnRow, justifyContent:"center", marginTop:24}}>
            <button style={S.btnSecondary} onClick={() => window.location.reload()}>
              Build Another Resume
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={S.scripture}>
        © 2026 Resh Consultancy LLC · Dallas, Texas · Vijay Anand Raj Kanaparthy · John 14:26
        <br/>Vijay is the ink. Claude is the pen. Yeshuwa is the Author.
      </div>

    </div>
  );
}
